// @ts-nocheck
import React, { useState, useEffect, useRef } from 'react';
import { HamburgerMenu } from './HamburgerMenu';
import { Breadcrumbs, type BreadcrumbItem } from './Breadcrumbs';
import { type NavItem } from './NavConfig';
import { ThemeToggle } from '@/components/ThemeToggle';
import { BackendErrorBanner } from '@/components/BackendErrorBanner';
import { useAuth } from '@/context/AuthContext';
import { SignalCellMark } from '@/components/brand/BrandMarks';

interface AppShellProps {
  currentView: string;
  breadcrumbTrail: BreadcrumbItem[];
  onNavigate: (viewKey: string) => void;
  contextualLinks: NavItem[];
  children: React.ReactNode;
  headerRight?: React.ReactNode;
  /** This user's home view key — where the brand tap lands. */
  homeKey?: string;
  /**
   * Mobile bottom bar (BottomNav), rendered by the App so it can compute the
   * role's items. When present, <main> reserves space so content never hides
   * behind the fixed bar. Receives the shell's menu opener via render prop.
   */
  bottomNav?: (openMenu: () => void) => React.ReactNode;
}

export const AppShell: React.FC<AppShellProps> = ({
  currentView,
  breadcrumbTrail,
  onNavigate,
  contextualLinks,
  children,
  headerRight,
  homeKey = 'map',
  bottomNav,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const headerRef = useRef<HTMLElement>(null);
  // The header brand is the signed-in organization's name, never a hardcoded
  // county: this is one product with many organizations. Before /auth/me
  // resolves (or for an orphaned account) it shows the product name.
  const { user } = useAuth();
  const brandName = user?.tenant_name || 'Voterra';

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [currentView]);

  // Publish --app-header-h CSS custom property so descendant sticky layers
  // (FilterBar, DataTable thead, MapView's --subregion-controls-h consumer)
  // can stack below the header without magic-number top offsets.
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;

    const measure = () => {
      const h = el.offsetHeight;
      document.documentElement.style.setProperty('--app-header-h', `${h}px`);
    };

    // Initial measurement (also re-runs when currentView changes because
    // Row 2 breadcrumbs hide on map view and Row 3 pills can appear/disappear).
    measure();

    const ro = new ResizeObserver(() => {
      measure();
    });
    ro.observe(el);

    return () => {
      ro.disconnect();
      // Intentionally do NOT clear --app-header-h: AppShell only unmounts
      // on full app teardown, and clearing would race with descendant sticky
      // layers that read the var.
    };
  }, [currentView]);

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  const handleNavigate = (viewKey: string) => {
    onNavigate(viewKey);
  };

  return (
    <div className="min-h-dvh bg-bg-base text-fg-primary flex flex-col">
      {/* Fixed top bar */}
      <header
        ref={headerRef}
        className={`sticky top-0 z-40 bg-bg-base/95 backdrop-blur border-b border-border-base transition-shadow duration-200 ${
          scrolled ? 'shadow-ds-md' : ''
        }`}
        role="banner"
      >
        {/* Global connectivity strip — visible only during a backend error */}
        <BackendErrorBanner />
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          {/* Row 1: hamburger + title + badge slot */}
          <div className="flex items-center justify-between min-h-14 py-1.5 gap-2">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <button
                onClick={toggleMenu}
                className="p-2 -ml-2 rounded-md hover:bg-bg-raised transition-colors text-fg-secondary hover:text-fg-primary min-h-11 min-w-11 flex items-center justify-center flex-shrink-0"
                aria-label="Toggle navigation menu"
                title="Navigation menu"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              {/* Brand wraps rather than truncating — clipping the org name reads
                  as broken. min-h-14 + ResizeObserver (--app-header-h) let the
                  header grow for the extra line on narrow screens. It is also
                  the universal "take me home" affordance (logo-goes-home). */}
              <h1 className="text-base sm:text-lg font-bold leading-tight">
                <button
                  onClick={() => handleNavigate(homeKey)}
                  className="text-fg-primary hover:text-accent-fg transition-colors text-left cursor-pointer flex items-center gap-2"
                  title="Go to your home screen"
                >
                  <SignalCellMark size={20} className="flex-shrink-0" />
                  <span>{brandName}</span>
                </button>
              </h1>
            </div>
            {/* Slot for DemoAccountBadge or other header elements. */}
            <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
              <ThemeToggle />
              {headerRight}
            </div>
          </div>

          {/* Row 2: breadcrumbs. The trail is empty on the home view (App
              computes it that way), so no special-casing by view key here. */}
          {breadcrumbTrail.length > 0 && (
            <Breadcrumbs trail={breadcrumbTrail} onNavigate={handleNavigate} />
          )}

          {/* Row 3: contextual links (only if there are any) */}
          {contextualLinks.length > 0 && (
            <div className="flex gap-2 pb-3 overflow-x-auto scrollbar-hide transition-all duration-200">
              {contextualLinks.map(link => (
                <button
                  key={link.key}
                  onClick={() => handleNavigate(link.key)}
                  className="flex items-center gap-1 text-xs bg-bg-raised hover:bg-bg-elevated px-3 py-2 min-h-11 sm:min-h-0 sm:py-1.5 rounded-full border border-border-base whitespace-nowrap transition-colors flex-shrink-0"
                  title={link.description}
                >
                  <link.Icon className="w-4 h-4" aria-hidden="true" />
                  <span>{link.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </header>

      {/* Hamburger menu */}
      <HamburgerMenu
        isOpen={menuOpen}
        onClose={() => setMenuOpen(false)}
        onNavigate={handleNavigate}
        currentView={currentView}
      />

      {/* Main content. With a bottom bar mounted (< lg), reserve its height +
          the safe area below it so nothing hides behind the fixed bar
          (fixed-element-offset); at lg+ the bar is hidden and the original
          padding returns. */}
      <main
        className={`flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 py-6 ${
          bottomNav
            ? 'pb-[calc(4.5rem+env(safe-area-inset-bottom))] lg:pb-[max(1.5rem,env(safe-area-inset-bottom))]'
            : 'pb-[max(1.5rem,env(safe-area-inset-bottom))]'
        }`}
      >
        {children}
      </main>

      {/* Mobile bottom bar */}
      {bottomNav?.(() => setMenuOpen(true))}
    </div>
  );
};
